import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Building,
  ClipboardCheck,
  Search,
  ChevronDown,
  ChevronRight,
  FileText,
  Home,
  ChevronLeft,
  X,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Khai báo kiểu Props cho Sidebar của Recruiter
interface RecruiterSidebarProps {
  activeTab: string;
  setActiveTab: (tab: any) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  isMobileSidebarOpen: boolean;
  setIsMobileSidebarOpen: (open: boolean) => void;
  isFindCandidatesExpanded: boolean;
  setIsFindCandidatesExpanded: (expanded: boolean) => void;
  isManagePostingsExpanded: boolean;
  setIsManagePostingsExpanded: (expanded: boolean) => void;
}

/**
 * Thanh Menu Điều Hướng Bên Trái (Sidebar) dành cho Nhà Tuyển Dụng
 * Hỗ trợ thu gọn/mở rộng và chế độ hiển thị trên thiết bị di động.
 */
export const RecruiterSidebar: React.FC<RecruiterSidebarProps> = ({
  activeTab,
  setActiveTab,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  isFindCandidatesExpanded,
  setIsFindCandidatesExpanded,
  isManagePostingsExpanded,
  setIsManagePostingsExpanded,
}) => {
  return (
    <>
      {/* --- 1. SIDEBAR CHO MÀN HÌNH MÁY TÍNH (DESKTOP) --- */}
      <aside
        className={cn(
          "bg-[#f97316] text-white hidden md:flex flex-col justify-between transition-all duration-300 relative z-30 shadow-lg h-screen shrink-0",
          isSidebarCollapsed ? "w-[80px]" : "w-[256px]"
        )}
      >
        <div>
          {/* Logo & Tiêu đề thương hiệu HRM */}
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0 shadow-inner">
              <Users size={20} className="text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div className="animate-fade-in">
                <h2 className="text-lg leading-tight tracking-wider font-bold">HRM</h2>
                <p className="text-[10px] text-white/70 font-semibold uppercase tracking-widest">Recruitment</p>
              </div>
            )}
          </div>

          {/* Danh Sách Mục Điều Hướng Nav Links */}
          <nav className="p-4 space-y-1">
            {/* Mục 1: Thông tin công ty */}
            <button
              onClick={() => {
                setActiveTab('settings');
                setIsMobileSidebarOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-200 text-left",
                activeTab === 'settings'
                  ? "bg-white text-[#f97316] shadow-md scale-[1.02]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              <Building size={18} className="shrink-0" />
              {!isSidebarCollapsed && <span>Thông tin công ty</span>}
            </button>

            {/* Mục 2: Quản lý ứng viên */}
            <button
              onClick={() => {
                setActiveTab('overview');
                setIsMobileSidebarOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-200 text-left",
                activeTab === 'overview'
                  ? "bg-white text-[#f97316] shadow-md scale-[1.02]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              <ClipboardCheck size={18} className="shrink-0" />
              {!isSidebarCollapsed && <span>Quản lý ứng viên</span>}
            </button>

            {/* Mục 3: Tìm thêm ứng viên (Dropdown Nối Dài) */}
            <div className="space-y-1">
              <button
                onClick={() => {
                  if (!isSidebarCollapsed) {
                    setIsFindCandidatesExpanded(!isFindCandidatesExpanded);
                  } else {
                    setIsSidebarCollapsed(false);
                    setIsFindCandidatesExpanded(true);
                  }
                }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-bold transition-all text-white/80 hover:text-white hover:bg-white/10 text-left"
              >
                <div className="flex items-center gap-3">
                  <Search size={18} className="shrink-0" />
                  {!isSidebarCollapsed && <span>Tìm thêm ứng viên</span>}
                </div>
                {!isSidebarCollapsed && (
                  isFindCandidatesExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                )}
              </button>

              {/* Danh sách con khi mở rộng phần Tìm ứng viên */}
              {isFindCandidatesExpanded && !isSidebarCollapsed && (
                <div className="pl-4 space-y-1 mt-1 border-l border-white/20 ml-6 animate-fade-in text-left">
                  <button
                    onClick={() => {
                      setActiveTab('candidates');
                      setIsMobileSidebarOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-4 py-2 rounded-xl text-xs font-bold transition-all",
                      activeTab === 'candidates'
                        ? "bg-white text-[#f97316] shadow-sm"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    )}
                  >
                    Tìm thêm ứng viên mới
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('saved_candidates');
                      setIsMobileSidebarOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-4 py-2 rounded-xl text-xs font-bold transition-all",
                      activeTab === 'saved_candidates'
                        ? "bg-white text-[#f97316] shadow-sm"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    )}
                  >
                    Ứng viên đã lưu
                  </button>
                </div>
              )}
            </div>

            {/* Mục 4: Quản lý tin (Dropdown Nối Dài) */}
            <div className="space-y-1">
              <button
                onClick={() => {
                  if (!isSidebarCollapsed) {
                    setIsManagePostingsExpanded(!isManagePostingsExpanded);
                  } else {
                    setIsSidebarCollapsed(false);
                    setIsManagePostingsExpanded(true);
                  }
                }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-bold transition-all text-white/80 hover:text-white hover:bg-white/10 text-left"
              >
                <div className="flex items-center gap-3">
                  <FileText size={18} className="shrink-0" />
                  {!isSidebarCollapsed && <span>Quản lý tin</span>}
                </div>
                {!isSidebarCollapsed && (
                  isManagePostingsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                )}
              </button>

              {/* Danh sách con khi mở rộng phần Quản lý tin */}
              {isManagePostingsExpanded && !isSidebarCollapsed && (
                <div className="pl-4 space-y-1 mt-1 border-l border-white/20 ml-6 animate-fade-in text-left">
                  <button
                    onClick={() => {
                      setActiveTab('postings');
                      setIsMobileSidebarOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-4 py-2 rounded-xl text-xs font-bold transition-all",
                      activeTab === 'postings'
                        ? "bg-white text-[#f97316] shadow-sm"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    )}
                  >
                    Quản lý tin đăng
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* Nút Điều Hướng Thu Gọn & Quay Về Trang Chủ */}
        <div className="p-4 border-t border-white/10 space-y-2.5">
          <Link
            to="/"
            className={cn(
              "w-full h-11 flex items-center justify-center rounded-2xl text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/15 transition-all gap-2 shadow-inner",
              isSidebarCollapsed ? "px-0" : "px-4"
            )}
          >
            <Home size={16} className="shrink-0" />
            {!isSidebarCollapsed && <span>Quay lại trang chủ</span>}
          </Link>

          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={cn(
              "w-full h-11 flex items-center justify-center rounded-2xl text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/15 transition-all gap-2 shadow-inner",
              isSidebarCollapsed ? "px-0" : "px-4"
            )}
          >
            {isSidebarCollapsed ? <ChevronRight size={16} className="shrink-0" /> : <ChevronLeft size={16} className="shrink-0" />}
            {!isSidebarCollapsed && <span>Thu gọn</span>}
          </button>
        </div>
      </aside>

      {/* --- 2. SIDEBAR DI ĐỘNG (MOBILE DRAWER OVERLAY) --- */}
      {isMobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 md:hidden flex animate-fade-in" onClick={() => setIsMobileSidebarOpen(false)}>
          <aside className="w-64 bg-gradient-to-b from-[#f97316] to-[#f97316] text-white flex flex-col justify-between h-full p-4 animate-in slide-in-from-left duration-200" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="flex items-center justify-between pb-6 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <Users size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm tracking-wider font-bold">HRM</h2>
                    <p className="text-[8px] text-white/60 font-semibold uppercase">Recruitment</p>
                  </div>
                </div>
                <button onClick={() => setIsMobileSidebarOpen(false)} className="text-white hover:bg-white/15 p-1 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <nav className="mt-4 space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('settings');
                    setIsMobileSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left",
                    activeTab === 'settings' ? "bg-white text-[#f97316]" : "text-white/80 hover:bg-white/10"
                  )}
                >
                  <Building size={16} />
                  <span>Thông tin công ty</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('overview');
                    setIsMobileSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left",
                    activeTab === 'overview' ? "bg-white text-[#f97316]" : "text-white/80 hover:bg-white/10"
                  )}
                >
                  <ClipboardCheck size={16} />
                  <span>Quản lý ứng viên</span>
                </button>

                <div className="space-y-0.5">
                  <button
                    onClick={() => setIsFindCandidatesExpanded(!isFindCandidatesExpanded)}
                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-white/80 hover:bg-white/10 text-left"
                  >
                    <div className="flex items-center gap-2.5">
                      <Search size={16} />
                      <span>Tìm thêm ứng viên</span>
                    </div>
                    {isFindCandidatesExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>

                  {isFindCandidatesExpanded && (
                    <div className="pl-3.5 space-y-0.5 mt-0.5 border-l border-white/20 ml-5 text-left">
                      <button
                        onClick={() => {
                          setActiveTab('candidates');
                          setIsMobileSidebarOpen(false);
                        }}
                        className={cn(
                          "w-full text-left px-3.5 py-2 rounded-lg text-[11px] font-bold transition-all",
                          activeTab === 'candidates' ? "bg-white text-[#f97316]" : "text-white/70 hover:bg-white/5"
                        )}
                      >
                        Tìm thêm ứng viên mới
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('saved_candidates');
                          setIsMobileSidebarOpen(false);
                        }}
                        className={cn(
                          "w-full text-left px-3.5 py-2 rounded-lg text-[11px] font-bold transition-all",
                          activeTab === 'saved_candidates' ? "bg-white text-[#f97316]" : "text-white/70 hover:bg-white/5"
                        )}
                      >
                        Ứng viên đã lưu
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-0.5">
                  <button
                    onClick={() => setIsManagePostingsExpanded(!isManagePostingsExpanded)}
                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-white/80 hover:bg-white/10 text-left"
                  >
                    <div className="flex items-center gap-2.5">
                      <FileText size={16} />
                      <span>Quản lý tin</span>
                    </div>
                    {isManagePostingsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>

                  {isManagePostingsExpanded && (
                    <div className="pl-3.5 space-y-0.5 mt-0.5 border-l border-white/20 ml-5 text-left">
                      <button
                        onClick={() => {
                          setActiveTab('postings');
                          setIsMobileSidebarOpen(false);
                        }}
                        className={cn(
                          "w-full text-left px-3.5 py-2 rounded-lg text-[11px] font-bold transition-all",
                          activeTab === 'postings' ? "bg-white text-[#f97316]" : "text-white/70 hover:bg-white/5"
                        )}
                      >
                        Quản lý tin đăng
                      </button>
                    </div>
                  )}
                </div>

                <Link
                  to="/"
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold text-white/85 hover:bg-white/10 transition-all mt-4 border border-white/15 bg-white/5"
                >
                  <ChevronLeft size={16} />
                  <span>Quay lại Trang chủ</span>
                </Link>
              </nav>
            </div>

            <div className="bg-white/10 rounded-xl p-3 text-center">
              <Megaphone size={16} className="text-white mx-auto mb-1.5" />
              <h4 className="text-[10px] font-black">HRM Portal</h4>
              <button className="bg-white text-[#f97316] text-[9px] font-black px-2 py-1 rounded mt-2 w-full">Tìm hiểu thêm</button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default RecruiterSidebar;
